/**
 * Job management contracts — used by the jobs handler.
 *
 * Covers CRUD over `leviathan_master.jobs` + per-job `job_runs` history + the
 * manual `POST /jobs/:id/run` invocation. ETL configuration lives in DynamoDB
 * (`{env}-leviathan-etl-configs`) keyed by the MySQL job id (1:1). Scheduling
 * is delegated to EventBridge Scheduler via the shared helper; this module
 * only expresses the HTTP surface.
 *
 * Response contracts are camelCase; DB snake_case is converted at the handler
 * edge before parsing.
 */
import { z } from 'zod';
import { ConnectorId, ISO8601Date, JobId, JobRunId, NonNegativeInt, TenantId, TenantSlug, UserId, } from '../primitives/index.js';
import { ConnectorType, JobRunStatus, JobStatus, JobTriggerType, } from '../enums/index.js';
import { JobTransformConfigSchema } from '../entities/job.js';
export { JobTransformConfigSchema } from '../entities/job.js';
// ============================================================================
// Shared building blocks
// ============================================================================
/**
 * Optional ETL configuration persisted to DynamoDB alongside the MySQL row.
 * Kept intentionally permissive: the executor is the canonical interpreter.
 */
export const EtlConfigPayloadSchema = z.object({
    name: z.string().optional(),
    description: z.string().optional(),
    source_type: z.string().optional(),
    logging: z.record(z.string(), z.unknown()).optional(),
    operations: z.array(z.record(z.string(), z.unknown())).optional(),
    reference_date: z.string().optional(),
});
// ============================================================================
// List
// ============================================================================
/** Row shape in `GET /jobs`. Super_admin-only fields are optional. */
export const JobSummarySchema = z.object({
    id: JobId,
    name: z.string(),
    description: z.string().nullable(),
    connectorId: ConnectorId,
    connectorName: z.string().nullable(),
    connectorType: ConnectorType.nullable(),
    schedule: z.string(),
    status: JobStatus,
    priority: z.number().int(),
    runs7d: z.number().int().nonnegative(),
    lastRunStatus: JobRunStatus.nullable(),
    lastRunAt: ISO8601Date.nullable(),
    createdAt: ISO8601Date.nullable(),
    updatedAt: ISO8601Date.nullable(),
    tenantId: TenantId.optional(),
    tenantName: z.string().optional(),
    tenantSlug: TenantSlug.optional(),
});
export const ListJobsResponseSchema = z.object({
    jobs: z.array(JobSummarySchema),
    total: z.number().int().nonnegative(),
});
// ============================================================================
// Get
// ============================================================================
/** Recent run returned alongside `GET /jobs/:id`. */
export const JobRecentRunSchema = z.object({
    id: JobRunId,
    status: JobRunStatus,
    startedAt: ISO8601Date.nullable(),
    completedAt: ISO8601Date.nullable(),
    rowsExtracted: z.number().int().nonnegative().nullable(),
    rowsLoaded: z.number().int().nonnegative().nullable(),
    errorMessage: z.string().nullable(),
});
export const JobDetailSchema = z.object({
    id: JobId,
    name: z.string(),
    description: z.string().nullable(),
    connectorId: ConnectorId,
    connectorName: z.string().nullable(),
    connectorType: ConnectorType.nullable(),
    schedule: z.string(),
    transformConfig: JobTransformConfigSchema.nullable(),
    status: JobStatus,
    priority: z.number().int(),
    createdAt: ISO8601Date.nullable(),
    updatedAt: ISO8601Date.nullable(),
    createdBy: UserId.nullable(),
});
export const GetJobResponseSchema = z.object({
    job: JobDetailSchema,
    recentRuns: z.array(JobRecentRunSchema),
});
// ============================================================================
// Create
// ============================================================================
/** `POST /jobs`. `etlConfig` is optionally mirrored into DynamoDB. */
export const CreateJobRequestSchema = z.object({
    name: z.string().min(1).max(100),
    description: z.string().optional(),
    connectorId: ConnectorId,
    schedule: z.string().min(1),
    transformConfig: JobTransformConfigSchema.optional(),
    priority: z.number().int().min(1).max(10).optional(),
    etlConfig: EtlConfigPayloadSchema.optional(),
});
export const CreateJobResponseSchema = z.object({
    message: z.string().min(1),
    job: z.object({
        id: JobId,
        name: z.string(),
        connectorId: ConnectorId,
        schedule: z.string(),
        status: JobStatus,
        priority: z.number().int(),
        hasEtlConfig: z.boolean(),
    }),
});
// ============================================================================
// Update
// ============================================================================
/** `PUT /jobs/:id`. All fields optional; handler requires at least one. */
export const UpdateJobRequestSchema = z
    .object({
    name: z.string().min(1).max(100),
    description: z.string().nullable(),
    schedule: z.string().min(1),
    transformConfig: JobTransformConfigSchema,
    status: JobStatus,
    priority: z.number().int().min(1).max(10),
})
    .partial();
export const UpdateJobResponseSchema = z.object({
    message: z.string().min(1),
    job: z.object({
        id: JobId,
        name: z.string(),
        schedule: z.string(),
        status: JobStatus,
        priority: z.number().int(),
        updatedAt: ISO8601Date.nullable(),
    }),
});
// ============================================================================
// Delete
// ============================================================================
export const DeleteJobResponseSchema = z.object({
    message: z.string().min(1),
    deletedJob: z.object({
        id: JobId,
        name: z.string(),
    }),
});
// ============================================================================
// Run (manual invocation)
// ============================================================================
/**
 * `POST /jobs/:id/run` — optional body fields. If omitted, the executor picks
 * defaults (`reference_date = TODAY`, no backfill window). `backfillYear`
 * derives start/end/reference from a single `YYYY` for fact rebuilds.
 */
export const RunJobRequestSchema = z
    .object({
    referenceDate: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    backfillYear: z.union([z.string(), z.number()]),
    phase: z.enum(['dimensions', 'facts']),
    lastRunOverride: z.string(),
})
    .partial();
export const RunJobResponseSchema = z.object({
    message: z.string().min(1),
    runId: JobRunId,
    jobId: JobId,
    status: z.literal('running'),
});
// ============================================================================
// Job Runs (history)
// ============================================================================
export const JobRunRecordSchema = z.object({
    id: JobRunId,
    status: JobRunStatus,
    startedAt: ISO8601Date.nullable(),
    completedAt: ISO8601Date.nullable(),
    rowsExtracted: z.number().int().nonnegative().nullable(),
    rowsTransformed: z.number().int().nonnegative().nullable(),
    rowsLoaded: z.number().int().nonnegative().nullable(),
    rowsRejected: z.number().int().nonnegative().nullable(),
    rowsProcessed: z.number().int().nonnegative().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    errorMessage: z.string().nullable(),
    triggerType: JobTriggerType.nullable(),
});
export const GetJobRunsResponseSchema = z.object({
    runs: z.array(JobRunRecordSchema),
    pagination: z.object({
        limit: z.number().int().positive(),
        offset: NonNegativeInt,
        total: NonNegativeInt,
        hasMore: z.boolean(),
    }),
});
//# sourceMappingURL=index.js.map